import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useState } from "react";
import PageLayout from "../../components/common/page-layout";
import { useCrownEnv } from "../../components/crown-data-import/env/env-context";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import ApiOrderPanel from "./components/api-order-panel";
import EdiPayloadPanel from "./components/edi-payload-panel";

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pt: 2,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 6,
  } satisfies SxProps<Theme>,
};

type OrderFlavor = "api" | "edi";

/**
 * Internal admin page that creates synthetic integration orders in the selected
 * target environment.
 *
 * Two tabs, one per integration flavor, because the two create paths take
 * different inputs: the API order is a typed form over the external
 * `POST /v2/orders` body, while the EDI order is a raw Stedi/Boomi payload that
 * the real endpoint accepts as-is. The flavor determines the order's source, and
 * source is what duplicate-order merging prioritizes by — EDI outranks API.
 *
 * Access requires the InternalTools write (or Master) permission, mirroring the
 * backend's `@InternalTools()` guard. The backend additionally refuses to run
 * either mutation on production; the banner and the disabled submit buttons
 * below say so before an operator fills the form out.
 */
const TestIntegrationOrdersPage = () => {
  const { canAccess, loading } = useIsInternalAdmin();
  const { env } = useCrownEnv();
  const [flavor, setFlavor] = useState<OrderFlavor>("api");

  const handleFlavorChange = useCallback((_: unknown, next: OrderFlavor) => {
    setFlavor(next);
  }, []);

  if (loading) {
    return (
      <PageLayout title="Test integration orders">
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout title="Test integration orders">
        <Box sx={styles.body}>
          <Alert severity="error">
            You do not have access to internal tools.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  const isProductionTarget = env === "prod";

  return (
    <PageLayout
      subtitle="Create synthetic EDI and API orders through the real integration create paths."
      title="Test integration orders"
    >
      <Box sx={styles.body}>
        {isProductionTarget && (
          <Alert severity="error">
            <AlertTitle>Not available against production</AlertTitle>
            These mutations fabricate partner traffic, so the backend refuses
            them on production. Switch the target environment in the launcher
            sidebar.
          </Alert>
        )}
        <Tabs value={flavor} onChange={handleFlavorChange}>
          <Tab label="API order" value="api" />
          <Tab label="EDI payload" value="edi" />
        </Tabs>
        {flavor === "api" ? (
          <ApiOrderPanel disabled={isProductionTarget} />
        ) : (
          <EdiPayloadPanel disabled={isProductionTarget} />
        )}
      </Box>
    </PageLayout>
  );
};

export default TestIntegrationOrdersPage;
